/**
 * PCM resampler for Sarvam streaming STT.
 *
 * Browsers give us microphone audio as 32-bit float samples at the OS sample
 * rate (typically 48 kHz). Sarvam wants 16-bit signed PCM at 16 kHz, mono,
 * little-endian, JSON-wrapped + base64-encoded:
 *
 *   {
 *     "audio": {
 *       "data": "<base64 of Int16 little-endian PCM bytes>",
 *       "sample_rate": "16000",
 *       "encoding": "audio/wav"
 *     }
 *   }
 *
 * This module turns a MediaStream into a stream of those JSON envelopes.
 *
 * Pipeline:
 *   MediaStream → MediaStreamSource → AudioWorkletNode → main thread
 *     (worklet does: resample float32@48k → float32@16k → int16 → chunk)
 *   main thread base64-encodes + JSON-wraps + fires onChunk callback
 *
 * The worklet runs in the dedicated audio rendering thread so it doesn't
 * block React renders, the silence detector, MediaRecorder, or anything
 * else on the main thread.
 *
 * Usage:
 *
 *   const resampler = await createPcmResampler({
 *     mediaStream,
 *     onChunk: (jsonStr) => sarvamWs.send(jsonStr),
 *     chunkDurationMs: 250,
 *   });
 *   await resampler.start();
 *   // ...interview happens...
 *   await resampler.stop();
 *
 * Design notes:
 *   - Worklet code is embedded as a string and loaded via Blob URL so we
 *     don't have to manage a separate static asset.
 *   - Linear interpolation for resampling — good enough for STT (Sarvam will
 *     happily transcribe; humans wouldn't notice the difference). Lanczos
 *     would be marginally better but ~4× more CPU.
 *   - Bounded internal buffer in the worklet — if main thread is slow,
 *     we drop frames instead of blowing memory.
 */

const SARVAM_TARGET_SAMPLE_RATE = 16000;

// AudioWorklet code as a string. This runs in the AudioWorkletGlobalScope —
// a completely separate JS context with its own globals, no DOM, no React.
// It receives Float32 audio buffers from the input port, resamples to 16kHz,
// quantizes to Int16, and posts Int16Array chunks back to the main thread.
const WORKLET_CODE = `
class SarvamPcmProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const opts = options.processorOptions || {};
    this.inputSampleRate = opts.inputSampleRate || sampleRate;
    this.targetSampleRate = opts.targetSampleRate || 16000;
    this.chunkSamples = opts.chunkSamples || 4000; // 250ms at 16kHz

    // Resampling ratio. e.g. 48000 → 16000 = 3.0 input samples per output sample.
    this.ratio = this.inputSampleRate / this.targetSampleRate;

    // Running buffer of resampled Float32 samples waiting to be packaged into
    // a chunk. Sized at 2x the chunk size — enough headroom for any single
    // process() call (which gives us 128 samples) to fit.
    this.outBuffer = new Float32Array(this.chunkSamples * 2);
    this.outBufferLen = 0;

    // Fractional sample index into the next input render quantum. Persists
    // across process() calls so resampling is continuous (no clicks at
    // chunk boundaries).
    this.inIndex = 0;
  }

  process(inputs, outputs, parameters) {
    const input = inputs[0];
    if (!input || input.length === 0 || !input[0]) {
      // No input connected yet — keep the processor alive.
      return true;
    }
    // Use channel 0 (mono). If stereo, we ignore channel 1.
    const channel = input[0];

    // Linear interpolation resampling. We walk through the input at the
    // resampling ratio, picking floating-point indices and interpolating
    // between adjacent samples.
    //
    // input is 128 samples (one render quantum). We may produce ~128/ratio
    // output samples per call (e.g. 128/3 ≈ 42 samples at 48k→16k).
    while (this.inIndex < channel.length) {
      const floorIdx = Math.floor(this.inIndex);
      const frac = this.inIndex - floorIdx;
      const s0 = channel[floorIdx];
      const s1 = channel[floorIdx + 1] !== undefined ? channel[floorIdx + 1] : s0;
      const resampled = s0 + (s1 - s0) * frac;

      this.outBuffer[this.outBufferLen++] = resampled;

      // If buffer hit the chunk size, flush a chunk to the main thread.
      if (this.outBufferLen >= this.chunkSamples) {
        this._flushChunk();
      }

      this.inIndex += this.ratio;
    }
    // Carry over the fractional position for the next call.
    this.inIndex -= channel.length;

    return true; // keep alive
  }

  _flushChunk() {
    // Quantize Float32 → Int16. Clip to int16 range as a defensive measure;
    // browsers rarely produce >1.0 from a mic, but headphones-as-mic can.
    const int16 = new Int16Array(this.chunkSamples);
    for (let i = 0; i < this.chunkSamples; i++) {
      let s = this.outBuffer[i];
      if (s > 1) s = 1;
      else if (s < -1) s = -1;
      // Multiply by 32767 not 32768 so the positive side maps cleanly.
      int16[i] = s < 0 ? Math.round(s * 32768) : Math.round(s * 32767);
    }
    // Transfer the underlying buffer to main thread — zero-copy.
    this.port.postMessage(int16.buffer, [int16.buffer]);

    // Slide the output buffer left to drop the flushed samples.
    const remaining = this.outBufferLen - this.chunkSamples;
    if (remaining > 0) {
      this.outBuffer.copyWithin(0, this.chunkSamples, this.outBufferLen);
    }
    this.outBufferLen = remaining;
  }
}

registerProcessor('sarvam-pcm-processor', SarvamPcmProcessor);
`;


/**
 * Convert a Uint8Array of arbitrary length to a base64 string.
 * btoa(String.fromCharCode(...arr)) blows up at ~64k args, so we chunk.
 */
function bytesToBase64(bytes) {
    let binary = "";
    const chunkSize = 0x8000; // 32KB at a time
    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, chunk);
    }
    return btoa(binary);
}


/**
 * Build the Sarvam-format JSON envelope from an Int16Array of PCM samples.
 * Kept as a separate helper so it's easy to unit-test or change the wire format.
 */
function buildSarvamAudioFrame(int16Array) {
    // Int16Array's underlying buffer is exactly the bytes we want — little-endian
    // because Web Audio runs on little-endian platforms (every supported OS).
    const bytes = new Uint8Array(int16Array.buffer, int16Array.byteOffset, int16Array.byteLength);
    return JSON.stringify({
        audio: {
            data: bytesToBase64(bytes),
            sample_rate: String(SARVAM_TARGET_SAMPLE_RATE),
            encoding: "audio/wav",
        },
    });
}


/**
 * Factory — returns a controller object with start() / stop() methods.
 *
 * @param {Object} opts
 * @param {MediaStream} opts.mediaStream - microphone stream (same one used by MediaRecorder is fine)
 * @param {(jsonChunk: string) => void} opts.onChunk - called once per ~chunkDurationMs of audio
 * @param {number} [opts.chunkDurationMs=250] - duration of each chunk in milliseconds
 * @param {object} [opts.logger=console] - optional logger with .log/.warn
 * @returns {Promise<{ start: () => Promise<void>, stop: () => Promise<void>, audioContext: AudioContext }>}
 */
export async function createPcmResampler({
    mediaStream,
    onChunk,
    chunkDurationMs = 250,
    logger = console,
}) {
    if (!mediaStream) throw new Error("createPcmResampler: mediaStream is required");
    if (typeof onChunk !== "function") throw new Error("createPcmResampler: onChunk callback is required");

    // Browser will give us audio at its native rate, typically 48000 on
    // desktop and most mobile. We resample inside the worklet to hit
    // Sarvam's required 16000.
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const inputSampleRate = audioContext.sampleRate;
    const chunkSamples = Math.round((SARVAM_TARGET_SAMPLE_RATE * chunkDurationMs) / 1000);

    logger.log(
        `[pcm-resampler] context sampleRate=${inputSampleRate}, target=${SARVAM_TARGET_SAMPLE_RATE}, chunk=${chunkSamples} samples (${chunkDurationMs}ms)`
    );

    // Load the worklet code from a Blob URL (no static asset needed).
    const workletBlob = new Blob([WORKLET_CODE], { type: "application/javascript" });
    const workletUrl = URL.createObjectURL(workletBlob);
    try {
        await audioContext.audioWorklet.addModule(workletUrl);
    } catch (e) {
        // Free the Blob URL even on failure to avoid leaks.
        URL.revokeObjectURL(workletUrl);
        await audioContext.close().catch(() => {});
        throw new Error(`[pcm-resampler] addModule failed: ${e?.message || e}`);
    }
    URL.revokeObjectURL(workletUrl);

    // Build the audio graph: source → worklet → destination (destination is
    // muted via gain=0 so we don't echo the candidate's mic into their
    // speakers). The destination connection is needed because AudioWorklet
    // process() doesn't run unless the node is in an active graph.
    const source = audioContext.createMediaStreamSource(mediaStream);
    const workletNode = new AudioWorkletNode(audioContext, "sarvam-pcm-processor", {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        processorOptions: {
            inputSampleRate,
            targetSampleRate: SARVAM_TARGET_SAMPLE_RATE,
            chunkSamples,
        },
    });
    const muteNode = audioContext.createGain();
    muteNode.gain.value = 0;

    // Main-thread side of the worklet port: receives Int16Array buffers,
    // converts to base64-wrapped JSON, hands off to caller via onChunk.
    // Defined as a named handler so stop() can null it and start() can
    // re-attach it across multiple start/stop cycles.
    const portMessageHandler = (event) => {
        try {
            const buf = event.data; // ArrayBuffer, transferred from worklet
            const int16 = new Int16Array(buf);
            const json = buildSarvamAudioFrame(int16);
            onChunk(json);
        } catch (e) {
            logger.warn("[pcm-resampler] onmessage handler failed:", e);
        }
    };
    workletNode.port.onmessage = portMessageHandler;

    // `isActive` is a single source of truth that can flip both ways. The
    // earlier one-way `started`/`stopped` flags meant once stopped you could
    // never start again — which broke askForMoreTime / handleNoResponse /
    // multi-question flows. Sarvam was getting no audio after the first stop.
    let isActive = false;
    let isDestroyed = false;

    async function start() {
        if (isDestroyed) {
            logger.warn("[pcm-resampler] start() called on destroyed resampler — ignored");
            return;
        }
        if (isActive) return;
        isActive = true;

        // Some browsers (especially Safari) start AudioContext in "suspended"
        // state until a user gesture. The interview already happens after a
        // gesture (candidate clicks "Start"), so resume() should succeed.
        // Also: stop() suspends the context, so we must resume on re-start.
        if (audioContext.state === "suspended") {
            try {
                await audioContext.resume();
            } catch (e) {
                logger.warn("[pcm-resampler] audioContext.resume() failed:", e);
            }
        }

        // (Re-)attach the port handler. We null it on stop() to avoid
        // delivering stale chunks to a closed consumer; re-attach on start.
        workletNode.port.onmessage = portMessageHandler;

        // Connect the graph. Order matters: source → worklet → mute → destination.
        // Safe to call repeatedly — connect() to an already-connected node is
        // a no-op per Web Audio spec.
        source.connect(workletNode);
        workletNode.connect(muteNode);
        muteNode.connect(audioContext.destination);
        logger.log("[pcm-resampler] started");
    }

    async function stop() {
        if (!isActive) return;
        isActive = false;
        try { source.disconnect(); } catch (_) {}
        try { workletNode.disconnect(); } catch (_) {}
        try { muteNode.disconnect(); } catch (_) {}
        // Drop the port handler so a final in-flight chunk doesn't fire after stop.
        try { workletNode.port.onmessage = null; } catch (_) {}
        // Don't close the AudioContext — that's terminal. Suspend so it can
        // be resumed on the next start().
        try { await audioContext.suspend(); } catch (_) {}
        logger.log("[pcm-resampler] stopped");
    }

    async function destroy() {
        isDestroyed = true;
        await stop();
        try { await audioContext.close(); } catch (_) {}
        logger.log("[pcm-resampler] destroyed");
    }

    return { start, stop, destroy, audioContext };
}
